import { RequestStatus, TransactionsStatus } from "../utils/commons";
import type { ITransaction } from "../interfaces/transactions";
import type { FundingMethod } from "../utils/sendCatalog";
import { useSessionStore } from "../store/useSessionStore";
import { useComposeStore } from "../store/useComposeStore";
import { claimDefaultPayoutSelection } from "../utils/claimPayoutCompose";
import i18n from "../locales/i18n";
import { mockSession } from "./mockData";
import { getMockTerms } from "./mockTerms";
import { sendComposeSeed } from "./sendMock";

/** Per-method fee + send limits for the flat funding picker (gallery). */
export interface FundRequestFundingMethodDetails {
  fee: number | null;
  minAmount: number | null;
  maxAmount: number | null;
}

export const fundRequestFundingDetails: Partial<
  Record<FundingMethod, FundRequestFundingMethodDetails>
> = {
  DEBIT_CARD: { fee: 2.1, minAmount: 10, maxAmount: 200 },
  CASH_DEPOSIT: { fee: 3.11, minAmount: 25, maxAmount: 500 },
  ACH: { fee: 1, minAmount: null, maxAmount: 2500 },
};

/** Debit-only SKUs so a saved CREDIT_CARD greys out in the picker. */
export const fundRequestAvailableFunding: FundingMethod[] = [
  "DEBIT_CARD",
  "CASH_DEPOSIT",
  "ACH",
];

/** Query flags carried by fund-request Transfer compose variants. */
export const GALLERY_AMOUNT_EDIT_PARAM = "galleryAmountEdit";
export const GALLERY_PAYOUT_EDIT_PARAM = "galleryPayoutEdit";
export const GALLERY_CURRENCY_EDIT_PARAM = "galleryCurrencyEdit";

const GALLERY_REQUESTER_PAYOUT = {
  type: "cash",
  payoutMethod: "CASH_PICKUP",
  cash: { payer: "ELEKTRA", provider: "Elektra" },
} as const;

/** Session + compose are global, so the override has to be undoable. */
let payoutEditApplied = false;

/**
 * Flips the claim session into the variant where the payer may retarget the
 * payout, seeding compose with the pinned destination so the row renders the
 * picker affordance instead of the locked grey chip.
 */
export const applyGalleryPayoutEdit = (
  permissions: { amount: boolean; payout: boolean; currency: boolean } = {
    amount: false,
    payout: true,
    currency: false,
  },
): boolean => {
  const current = useSessionStore.getState().session;
  if (!current.data) return false;
  payoutEditApplied = true;

  const userData = {
    ...current.data.userData,
    name: current.data.userData.name ?? {
      firstName: "Maria",
      lastName: "Lopez",
    },
    paymentMethod: GALLERY_REQUESTER_PAYOUT,
  };

  useSessionStore.getState().setSession({
    ...current,
    data: {
      ...current.data,
      userData,
      config: {
        ...current.data.config,
        flow: "fund-request",
        allowAmountEdit: permissions.amount,
        allowPayoutEdit: permissions.payout,
        allowDestinationCurrencyEdit: permissions.currency,
      },
    },
    status: RequestStatus.LOADED,
    error: null,
  });

  const defaultSelection = claimDefaultPayoutSelection(userData);
  if (permissions.payout && defaultSelection) {
    useComposeStore.getState().setSelection(defaultSelection);
  }
  useComposeStore
    .getState()
    .setDestinationCurrency(current.data.config.destinationCurrency ?? "MXN");
  useComposeStore
    .getState()
    .setSourceCurrency(current.data.config.sourceCurrency ?? "USD");

  return true;
};

/**
 * Restores the seeded claim so the locked Transfer screen never inherits the
 * editable variant's destination. No-op until the override has been applied.
 */
export const revertGalleryPayoutEdit = (): void => {
  if (!payoutEditApplied) return;
  payoutEditApplied = false;

  // Keep localized gallery terms for the active UI language (avoids a
  // one-frame English flash before `useGalleryTerms` re-syncs).
  useSessionStore.getState().setSession({
    data: {
      ...mockSession,
      terms: getMockTerms(i18n.resolvedLanguage ?? i18n.language),
    },
    status: RequestStatus.LOADED,
    error: null,
  });
  useComposeStore.getState().setSelection(sendComposeSeed.selection);
  useComposeStore
    .getState()
    .setDestinationCurrency(mockSession.config.destinationCurrency ?? "MXN");
  useComposeStore
    .getState()
    .setSourceCurrency(mockSession.config.sourceCurrency ?? "USD");
};

/** Mock operations for the fund-request transactions gallery screen. */
/** Sender/payer perspective: list shows sendAmount + sourceCurrency. */
export const fundRequestMockTransactions: ITransaction[] = [
  {
    operationId: "op_gallery_completed",
    status: TransactionsStatus.COMPLETED,
    amount: "1,400.00",
    currency: "USD",
    date: "10/06/26",
  },
  {
    operationId: "op_gallery_processing",
    status: TransactionsStatus.PROCESSING,
    amount: "1,143.50",
    currency: "USD",
    date: "05/06/26",
  },
  {
    operationId: "op_gallery_pending",
    status: TransactionsStatus.PENDING,
    amount: "68.89",
    currency: "USD",
    date: "04/06/26",
  },
  {
    operationId: "op_gallery_failed",
    status: TransactionsStatus.FAILED,
    amount: "50.00",
    currency: "USD",
    date: "03/06/26",
  },
  {
    operationId: "op_gallery_canceled",
    status: TransactionsStatus.CANCELED,
    amount: "35.00",
    currency: "USD",
    date: "02/06/26",
  },
  {
    operationId: "op_gallery_expired",
    status: TransactionsStatus.EXPIRED,
    amount: "20.00",
    currency: "USD",
    date: "01/06/26",
  },
  {
    operationId: "op_gallery_pending_cash",
    status: TransactionsStatus.PENDING,
    amount: "1,023.13",
    currency: "USD",
    date: "08/06/26",
    cashDepositPending: true,
    depositName: "Jane Doe",
  },
];

export interface FundRequestMockReceipt {
  operationId: string;
  sendAmount: number;
  receiveAmount: number;
  totalCost: number;
  fee: number;
  fundingFee: number;
  transactionFee: number;
  taxFee: number;
  totalFee?: number;
  exchangeRate: number;
  sourceCurrency: string;
  destinationCurrency: string;
  status: string;
  statusReason?: string;
  createdAt: string;
  receiverName: string;
  cancellation?: {
    allowed: boolean;
    mode?: "void" | "request";
    expiresAt?: string;
  };
  references?: Array<{
    type: string;
    value: string;
    label?: Record<string, string>;
    presentation: string;
  }>;
}

export const fundRequestMockReceipts: Record<string, FundRequestMockReceipt> = {
  op_gallery_completed: {
    operationId: "op_3a6ca1b2c3d4",
    sendAmount: 400,
    receiveAmount: 6968,
    totalCost: 401,
    fee: 1,
    fundingFee: 1,
    transactionFee: 0,
    taxFee: 0,
    exchangeRate: 17.42,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
    status: "completed",
    createdAt: "2026-06-10T12:00:00.000Z",
    receiverName: "Jane Doe",
    references: [
      {
        type: "PAYOUT_CODE",
        value: "998877",
        presentation: "PROMINENT",
        label: { en: "Payout code", es: "Clave de cobro", fr: "Code de paiement" },
      },
    ],
  },
  op_gallery_processing: {
    operationId: "op_8f21e90ab4c1",
    sendAmount: 143.5,
    receiveAmount: 2500,
    totalCost: 144.5,
    fee: 1,
    fundingFee: 1,
    transactionFee: 0,
    taxFee: 0,
    exchangeRate: 17.42,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
    status: "processing",
    createdAt: "2026-06-05T12:00:00.000Z",
    receiverName: "Jane Doe",
    cancellation: {
      allowed: true,
      mode: "request",
      expiresAt: "2099-01-01T00:00:00.000Z",
    },
  },
  op_gallery_failed: {
    operationId: "op_c41d77e2a019",
    sendAmount: 50,
    receiveAmount: 800,
    totalCost: 51,
    fee: 1,
    fundingFee: 1,
    transactionFee: 0,
    taxFee: 0,
    exchangeRate: 17.42,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
    status: "failed",
    statusReason: "provider_rejected",
    createdAt: "2026-06-03T12:00:00.000Z",
    receiverName: "Jane Doe",
  },
  op_gallery_canceled: {
    operationId: "op_b90e12d4f833",
    sendAmount: 35,
    receiveAmount: 550,
    totalCost: 36,
    fee: 1,
    fundingFee: 1,
    transactionFee: 0,
    taxFee: 0,
    exchangeRate: 17.42,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
    status: "cancelled",
    createdAt: "2026-06-02T12:00:00.000Z",
    receiverName: "Jane Doe",
  },
  op_gallery_expired: {
    operationId: "op_11a8c6d0e247",
    sendAmount: 20,
    receiveAmount: 300,
    totalCost: 21,
    fee: 1,
    fundingFee: 1,
    transactionFee: 0,
    taxFee: 0,
    exchangeRate: 17.42,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
    status: "expired",
    createdAt: "2026-06-01T12:00:00.000Z",
    receiverName: "Jane Doe",
  },
};

/** Default operation receipt for fund-request gallery static routes. */
export const DEFAULT_FUND_REQUEST_RECEIPT_ID = "op_gallery_completed";

/**
 * Resolves mock operation id from react-router param or the last URL segment.
 */
export const resolveFundRequestReceiptId = (
  paramId: string | undefined,
  pathname: string
): string => {
  if (paramId && fundRequestMockReceipts[paramId]) return paramId;
  const lastSegment = pathname.split("/").filter(Boolean).pop();
  if (lastSegment && fundRequestMockReceipts[lastSegment]) return lastSegment;
  return DEFAULT_FUND_REQUEST_RECEIPT_ID;
};

export const getFundRequestMockReceipt = (
  paramId: string | undefined,
  pathname: string
): FundRequestMockReceipt =>
  fundRequestMockReceipts[resolveFundRequestReceiptId(paramId, pathname)] ??
  fundRequestMockReceipts[DEFAULT_FUND_REQUEST_RECEIPT_ID];
